import React, { useState, useEffect, useMemo } from "react";
import { getToken } from "../utils/tokenUtils";
import apiClient from "../utils/apiUtils";
import { UsersContext } from "./UsersContext";

export function UsersProvider({ children }) {
  const [users, setUsers] = useState([]);
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allUsersWithEmployeeIds, setAllUsersWithEmployeeIds] = useState([]);

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`users?page=${page}`);

      const responseData = response.data?.data || response.data;
      const usersList = Array.isArray(responseData)
        ? responseData
        : responseData.data || [];

      setUsers(usersList);
      setPagination({
        currentPage: responseData.current_page || 1,
        totalPages: responseData.last_page || 1,
        totalItems: responseData.total || 0,
        perPage: responseData.per_page || 10,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users from the API
  useEffect(() => {
    fetchUsers();
  }, [token]);

  const addUser = async (user) => {
    try {
      const response = await apiClient.post("users", user);
      setUsers((prevUsers) => [
        response.data?.data || response.data,
        ...prevUsers,
      ]);
      return { success: true };
    } catch (error) {
      console.error("Error adding user:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to add user.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Update existing user
  const updateUser = async (updatedUser) => {
    try {
      const response = await apiClient.put(
        `users/${updatedUser.id}`,
        updatedUser,
      );
      const updatedUserData = response.data?.data || response.data; // Extract updated user from API response
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          (user.user_id || user.id) ===
          (updatedUserData.user_id || updatedUserData.id)
            ? { ...user, ...updatedUserData }
            : user,
        ),
      );
      return { success: true };
    } catch (error) {
      console.error("Error updating user:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to add user.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Delete user
  const deleteUser = async (id) => {
    try {
      await apiClient.delete(`users/${id}`);
      setUsers((prevUsers) =>
        prevUsers.filter((user) => (user.user_id || user.id) !== id),
      );
      return { success: true };
    } catch (error) {
      // console.error("Error deleting user:", error);
      console.error("Error deleting user:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to delete user.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Fetch all users with employee IDs (no pagination)
  const fetchAllUsersWithEmployeeIds = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("users-with-employee-ids");
      setAllUsersWithEmployeeIds(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Error fetching all users with employee ids:", error);
    } finally {
      setLoading(false);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      pagination,
      loading,
      users,
      error,
      fetchUsers,
      addUser,
      updateUser,
      deleteUser,
      allUsersWithEmployeeIds,
      fetchAllUsersWithEmployeeIds,
    }),
    [users, loading, allUsersWithEmployeeIds, pagination, error], // Recalculate when dependencies change
  );

  return (
    <UsersContext.Provider value={value}>{children}</UsersContext.Provider>
  );
}
